import { getLogger } from '@cld/upload-widget-common';
import appConfig from 'appConfig';
import t from 'common/i18n';
import consts from '../data/consts';

const logger = getLogger();
const { RESOURCES } = consts;

const getLoginStatus = () =>
  new Promise((resolve) => {
    self.FB.getLoginStatus((response) => resolve(response), false);
  });

const makeRequest = (...args) =>
  new Promise((resolve, reject) => {
    self.FB.api(...args, (res) => (res && res.error ? reject(res.error) : resolve(res)));
  });

const getUser = (auth) => makeRequest(`/${auth.authResponse.userID}`);

const login = () =>
  new Promise((resolve) => {
    logger.log('[facebookService]: about to log in to FB');
    self.FB.login((response) => resolve(response), { scope: 'user_photos' });
  });

const logout = () =>
  new Promise((resolve) => {
    logger.log('[facebookService]: about to log out from FB');

    self.FB.logout((response) => {
      // Were cheating Facebook and enforce full logout/login.
      // Otherwise after a logout, there a need to login any page reload. There probably a nicer way to handle it.
      document.cookie = `fblo_${appConfig.facebook.appId}=n`;
      resolve(response);
    });
  });

const getImagesFromResponse = (response) =>
  response.data.map((item) => {
    const image = item.images[0];
    const thumbnail = item.images.slice(-1)[0];
    const { width } = image;
    const { height } = image;

    return {
      id: image.source,
      thumbnail: thumbnail.source,
      url: image.source,
      link: image.source,
      title: width && height ? `${width} × ${height}` : '',
      subtitle: item.name,
      selected: item.selected,
    };
  });

const getAlbumsFromResponse = (response) =>
  response.data.map((item) => ({
    id: item.id,
    thumbnail: item.picture.data.url,
    url: item.picture.data.url,
    title: item.name,
    subtitle: t('facebook.album_subtitle', { count: item.count }),
    selected: item.selected,
  }));

const getApiItems = async (resOptions, options) => {
  const { resource, type, fields, id } = resOptions;
  const after = options.extend ? `&after=${options.cursor || ''}` : '';
  const urlId = id || options.auth.authResponse.userID;
  const urlType = type ? `type=${type}&` : '';
  const fbResource = `/${urlId}/${resource}?${urlType}fields=id,name${fields}${after}`;

  let items;
  let cursor = null;

  logger.log(`[facebookService]: about to retrieve items type: ${resource} from: ${fbResource}`);

  if (!options.extend || (options.extend && options.cursor)) {
    const response = await makeRequest(fbResource);

    items = resource === RESOURCES.ALBUMS ? getAlbumsFromResponse(response) : getImagesFromResponse(response);

    logger.log(`[facebookService]: retrieved ${items.length} for resource: ${resource}`);

    if (options.extend) {
      items = options.items ? options.items.concat(items) : items; // Concat new results on infinite scroll.
    }

    cursor = response.paging ? response.paging.cursors.after : null;
  } else if (options.extend) {
    items = options.items || [];
  }

  return {
    items,
    cursor,
  };
};

const getUploadedPhotos = async (options) =>
  getApiItems(
    {
      resource: RESOURCES.PHOTOS,
      type: consts.RES_TYPES.UPLOADED,
      fields: ',images',
    },
    options
  );

const getTaggedPhotos = async (options) =>
  getApiItems(
    {
      resource: RESOURCES.PHOTOS,
      type: consts.RES_TYPES.TAGGED,
      fields: ',images',
    },
    options
  );

const getAlbums = async (options) =>
  getApiItems(
    {
      resource: consts.RESOURCES.ALBUMS,
      fields: ',picture,count',
    },
    options
  );

const getAlbumPhotos = (options) =>
  getApiItems(
    {
      resource: consts.RESOURCES.PHOTOS,
      fields: ',images',
      id: options.id,
    },
    options
  );

const getAlbumData = (options) => {
  if (!options.id) {
    // for flow's sake
    throw new Error('[facebookService]: album id is required to retrieve album data!');
  }

  const id = options.id || ''; // flow is so stupid!

  logger.log(`[facebookService]: about to retrieve album data for id: ${id}`);
  return makeRequest(`/${id}/?fields=id,name,created_time`);
};

const getAlbum = (options) =>
  Promise.all([getAlbumData(options), getAlbumPhotos(options)]).then((results) => ({
    album: results[0],
    ...results[1],
  }));

export default () => {
  logger.log('[facebookService]: creating facebook api');

  let initPromise;

  const init = (options) => {
    if (!initPromise) {
      initPromise = new Promise((resolve) => {
        self.fbAsyncInit = () => {
          self.FB.init({
            appId: options.externalAppId || appConfig.facebook.appId,
            xfbml: false,
            version: appConfig.facebook.version,
          });

          resolve();
        };

        if (!self.FB) {
          ((d, s, id) => {
            if (!d.getElementById(id)) {
              let js;
              const fjs = d.getElementsByTagName(s)[0];
              js = d.createElement(s);
              js.id = id;
              js.src = appConfig.facebook.sdk;
              if (fjs.parentNode) {
                fjs.parentNode.insertBefore(js, fjs);
              }
            }
          })(document, 'script', 'facebook-jssdk');
        }
      });
    }

    return initPromise;
  };

  return {
    init,
    getLoginStatus,
    makeRequest,
    getUser,
    login,
    logout,
    getUploadedPhotos,
    getTaggedPhotos,
    getAlbums,
    getAlbum,
  };
};
